// BL-124: dragging a visit on the board, kept apart from React so it is tested on its own. A visit that
// hasn't started moves along the time axis (snapping to the company's slot, dispatch.slotMinutes), to
// another technician's lane, or by either edge to change its length; the keyboard does the same with
// the arrows. While it's dragged, a note says where it would land and what letting go does. The drop
// is one of three: clean (saved at once, with Undo), needs a look (the Move panel opens with it), or
// can't (it goes back, saying why). The server still decides: a clean drop only sends the actions.
// BL-158 (Amir 26 Oct): a visit that has started moves too, on its own lane and day only: its times
// change (it ended early, the tech got there late), never who has it. While work goes on its end can't
// be before now. A visit that is over (done, or started on a day gone by) changes only after a
// warning, the same check panel a drop that needs a look opens. Both send appointments.retime.

import {
  addDays,
  instantAt,
  localDateOf,
  sentences,
  timeText,
  windowText as kernelWindow,
} from '@flowology/kernel';
import { licenceShortfalls, shortfallWords } from '@flowology/domain';
import { parseTypedTime } from '@flowology/ui-web/fl';
import { minutesText } from './books/timesheet-model.ts';
import {
  clock,
  DAY_START_HOUR,
  dropResult,
  localHours,
  shownSpan,
  type Board,
  type BoardTech,
  type BoardVisit,
  type TechBlock,
} from './board-model.ts';

/** dispatch.slotMinutes's own default, for a board from before it was sent. */
export const DEFAULT_SLOT_MINUTES = 15;
/** How long a finger holds still on a visit to pick it up; a swipe before then scrolls the board. */
export const HOLD_MS = 380;
/** How far a mouse moves before a press becomes a drag (so a click still opens the visit). */
export const MOUSE_SLOP_PX = 5;
/** How far a finger may wander while holding before it counts as a swipe. */
export const TOUCH_SLOP_PX = 8;
/** How long a clean drop's Undo stays (Amir's choice 1, 24 Oct). */
export const UNDO_MS = 10_000;

/** A visit's place while it's dragged: its lane (null: No one yet) and its times, as minutes of the board's day. */
export interface DragPlace {
  techId: string | null;
  start: number;
  end: number;
}

/** What is held: the visit's middle (move), or one of its ends. */
export type DragEdge = 'move' | 'start' | 'end';
/** The hand doing it, for the note's last line. */
export type DragHow = 'mouse' | 'touch' | 'key';

/** A visit's shortest length on the board: the slot, and never under half an hour. */
export const minLength = (slot: number) => Math.max(30, slot);

/** Snaps minutes to the slot (15 min: 9:07 → 9:00, 9:08 → 9:15). */
export function snap(minute: number, slot: number): number {
  const s = slot > 0 ? slot : DEFAULT_SLOT_MINUTES;
  return Math.round(minute / s) * s;
}

/** Minutes since the board day's local midnight of an instant; the next day's run on past 24 h. */
export function minuteOf(instant: string, date: string, timeZone: string): number {
  const m = Math.round(localHours(instant, timeZone) * 60);
  return localDateOf(instant, timeZone) > date ? m + 1440 : m;
}

/** The instant at a minute of the board's day (past midnight: the next day's). */
export function instantOfMinute(date: string, minute: number, timeZone: string): string {
  const day = minute >= 1440 ? addDays(date, 1) : date;
  const m = ((minute % 1440) + 1440) % 1440;
  const hh = String(Math.floor(m / 60)).padStart(2, '0');
  const mm = String(m % 60).padStart(2, '0');
  return instantAt(day, `${hh}:${mm}`, timeZone);
}

/** The minute under a point across a lane drawn from DAY_START_HOUR to `endHour`. */
export function minuteAtX(x: number, lane: { left: number; width: number }, endHour: number): number {
  const from = DAY_START_HOUR * 60;
  const to = endHour * 60;
  const share = lane.width > 0 ? (x - lane.left) / lane.width : 0;
  return from + share * (to - from);
}

/**
 * Where a drag puts the visit: held by its middle, it keeps its length and moves with the pointer
 * (`grab`: how far into the visit it was held); held by an end, that end moves and the other stays,
 * never shorter than the slot. Always inside the board's day, snapped to the slot.
 */
export function dragTo(
  edge: DragEdge,
  from: { start: number; end: number },
  pointer: number,
  grab: number,
  bounds: { from: number; to: number },
  slot: number,
): { start: number; end: number } {
  const len = from.end - from.start;
  const min = minLength(slot);
  if (edge === 'move') {
    const s = Math.min(Math.max(snap(pointer - grab, slot), bounds.from), bounds.to - len);
    return { start: s, end: s + len };
  }
  const m = snap(pointer, slot);
  if (edge === 'start') return { start: Math.min(Math.max(m, bounds.from), from.end - min), end: from.end };
  return { start: from.start, end: Math.max(Math.min(m, bounds.to), from.start + min) };
}

/**
 * The keyboard's move (BL-124): left and right move the visit a slot, Shift with them moves its end,
 * up and down give it to the technician above or below (`lanes`, in the board's order). Null for any
 * other key.
 */
export function keyStep(
  at: DragPlace,
  key: string,
  shift: boolean,
  lanes: readonly string[],
  bounds: { from: number; to: number },
  slot: number,
): DragPlace | null {
  const step = key === 'ArrowLeft' ? -slot : key === 'ArrowRight' ? slot : 0;
  const len = at.end - at.start;
  if (step && shift)
    return { ...at, end: Math.min(Math.max(at.end + step, at.start + minLength(slot)), bounds.to) };
  if (step) {
    const s = Math.min(Math.max(at.start + step, bounds.from), bounds.to - len);
    return { ...at, start: s, end: s + len };
  }
  if (key !== 'ArrowUp' && key !== 'ArrowDown') return null;
  if (!lanes.length) return at;
  const i = at.techId ? lanes.indexOf(at.techId) : -1;
  const next = i < 0 ? 0 : Math.min(Math.max(i + (key === 'ArrowDown' ? 1 : -1), 0), lanes.length - 1);
  return { ...at, techId: lanes[next] ?? at.techId };
}

/**
 * What an arrow that moved nothing says (QA-706 5): the visit is at the board's edge (its first or
 * last lane, the day's start or end, or as short as it goes), so the keyboard isn't left guessing.
 * Null when the arrow moved it.
 */
export function keyEdge(
  at: DragPlace,
  next: DragPlace,
  key: string,
  shift: boolean,
  rule: TimeRule = 'free',
): string | null {
  if (next.techId !== at.techId || next.start !== at.start || next.end !== at.end) return null;
  if (key === 'ArrowUp') return 'That’s the first technician on the board.';
  if (key === 'ArrowDown') return 'That’s the last technician on the board.';
  if (key === 'ArrowLeft')
    return shift ? 'That’s as short as a visit goes.' : 'That’s the start of the day on the board.';
  // a started visit stays on its day (BL-158): there's no other day to send it to
  if (rule === 'started' || rule === 'over')
    return 'That’s the end of the day on the board. A visit that has started stays on its day.';
  return 'That’s the end of the day on the board. To move it to another day, use Move in its panel.';
}

/**
 * Which rules a visit's drag is under (BL-158, as appointments.retime's retimeRule on the server):
 * 'free' hasn't started (anywhere, any lane); 'started', work under way on its own day (its times only,
 * the end not before now); 'over', done (closed, or paused with the work done) or started on a day
 * gone by (its times only, after a warning); 'none', cancelled.
 */
export type TimeRule = 'free' | 'started' | 'over' | 'none';
export function timeRule(
  v: Pick<BoardVisit, 'status' | 'start' | 'pauseReason'>,
  now: string,
  timeZone: string,
): TimeRule {
  if (v.status === 'cancelled') return 'none';
  if (v.status === 'closed' || (v.status === 'paused' && v.pauseReason === 'work_done')) return 'over';
  if (v.status !== 'working' && v.status !== 'paused') return 'free';
  return localDateOf(v.start, timeZone) < localDateOf(now, timeZone) ? 'over' : 'started';
}

/** Why a visit can't be picked up at all (cancelled), or null when it can. */
export function cantPickUp(v: Pick<BoardVisit, 'status' | 'customerName'>): string | null {
  if (v.status === 'cancelled') return `${v.customerName}’s visit is cancelled.`;
  return null;
}

const toRad = (d: number) => (d * Math.PI) / 180;
/**
 * A quick drive estimate between two map points, in whole minutes: the straight line, a third longer
 * for the roads, at 45 km/h, plus 3 minutes to park. Ours alone (the board's own points): dragging
 * never asks a paid traffic service. The Move panel's full check is the maps plan's (FLO-23).
 */
export function driveMinutes(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  const km = 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
  return Math.ceil(((km * 1.35) / 45) * 60 + 3);
}

export type OutcomeKind = 'none' | 'clean' | 'look' | 'refuse';
export interface NoteLine {
  /** true: fine; false: a problem; null: not checked */
  ok: boolean | null;
  text: string;
}
export interface Outcome {
  kind: OutcomeKind;
  /** "1:30–3 p.m., Sam Kowal", with the length when it changed */
  head: string;
  lines: NoteLine[];
  /** a drop that can't be made: the line said when it goes back */
  reason?: string;
  /** BL-158: a started visit's times ('started'), or one that is over, changed after its warning ('over') */
  retime?: 'started' | 'over';
  /** A typed time's refusal: the field it's about (QA-766: a start after now is the start's) */
  field?: 'start' | 'end';
}

export interface MoveCheck {
  board: Pick<Board, 'technicians' | 'unassigned' | 'licenceRule'>;
  visit: BoardVisit;
  /** The lane it was picked up from (null: No one yet, or the tray). */
  fromTechId: string | null;
  to: DragPlace;
  /** The board's day, and now, both for the company's time zone. */
  date: string;
  now: string;
  timeZone: string;
}

const firstName = (name: string) => name.split(/\s+/)[0] ?? name;

/** A technician's day on the board: their hours and what takes them away. */
const dayOf = (t: BoardTech | undefined, date: string) => t?.days?.find((d) => d.date === date);

const overlaps = (aStart: number, aEnd: number, bStart: number, bEnd: number) =>
  aStart < bEnd && bStart < aEnd;

/**
 * BL-027 for a move (QA-691): the licences the visit needs that nobody on it would hold once
 * `toTechId` takes the place of `fromTechId`, valid on the board's day, by the rule the server and the
 * iPad use (@flowology/domain). In words, as the server's gaps read, with `known` false when someone on
 * it isn't on the board (their licences unknown here: the server still decides). None from a server
 * that doesn't send what's needed.
 */
export function licenceGapsAfter(
  board: Pick<Board, 'technicians'>,
  v: Pick<BoardVisit, 'techIds' | 'licenceNeeds'>,
  fromTechId: string | null,
  toTechId: string,
  day: string,
  today: string,
  timeZone: string,
): { texts: string[]; known: boolean } {
  const needs = v.licenceNeeds ?? [];
  if (!needs.length) return { texts: [], known: true };
  const crew = [...new Set([...v.techIds.filter((t) => t !== fromTechId), toTechId])];
  const on = board.technicians.filter((t) => crew.includes(t.id));
  if (on.some((t) => !t.licences)) return { texts: [], known: true };
  const held = on.flatMap((t) => (t.licences ?? []).map((l) => ({ staffId: t.id, ...l })));
  const texts = licenceShortfalls(
    needs.map((n) => n.kindId),
    crew,
    held,
    day,
  ).map((s) => {
    const holder = s.holder ? (on.find((t) => t.id === s.holder!.staffId)?.name ?? null) : null;
    const kindName = needs.find((n) => n.kindId === s.kindId)?.kindName ?? 'A licence';
    return shortfallWords(
      { kindName, holderName: holder, expiresOn: s.holder?.expiresOn ?? null },
      day,
      today,
      timeZone,
    ).text;
  });
  return { texts, known: on.length === crew.length };
}

/**
 * What letting go there means (BL-124). Can't: a technician's lane it's already on from another, No
 * one yet (taking it off someone is the panel's), a time already gone, time
 * away, or someone already on a started or finished visit then. Needs a look: it runs into another
 * visit, leaves the customer's arrival window, goes past the technician's hours, the drive doesn't
 * fit, or it changes hands while it's on the way. Clean: none of those. None: where it already is.
 */
export function classifyMove(c: MoveCheck): Outcome {
  const { visit: v, to, date, timeZone: tz } = c;
  const start = instantOfMinute(date, to.start, tz);
  const end = instantOfMinute(date, to.end, tz);
  const tech = to.techId ? c.board.technicians.find((t) => t.id === to.techId) : undefined;
  const name = tech?.name ?? (to.techId ? 'A technician' : 'No one yet');
  const oldLen = Math.round((Date.parse(v.end) - Date.parse(v.start)) / 60_000);
  const len = to.end - to.start;
  const head = `${kernelWindow(start, end, tz)}, ${name}${len !== oldLen ? `, ${minutesText(len)}` : ''}`;
  const refuse = (reason: string, field?: 'start' | 'end'): Outcome => ({
    kind: 'refuse',
    head,
    lines: [{ ok: false, text: reason }],
    reason,
    ...(field ? { field } : {}),
  });
  const picked = cantPickUp(v);
  if (picked) return refuse(picked);
  const rule = timeRule(v, c.now, tz);
  if (rule === 'started' || rule === 'over') return classifyRetime(c, rule, head, refuse);
  const sameTech = to.techId === c.fromTechId;
  const sameTime = start === v.start && end === v.end;
  if (sameTech && sameTime) return { kind: 'none', head, lines: [] };
  // No one yet takes nothing from a drag (Amir's choice 6, 24 Oct)
  if (!to.techId)
    return refuse(
      v.techIds.length
        ? 'Drop it on a technician’s lane. To take it off someone, use the visit’s panel.'
        : 'Drop it on a technician’s lane to give it to someone.',
    );
  if (to.techId && !sameTech && v.techIds.includes(to.techId))
    return refuse(`${name} is already on this visit.`);
  if (!sameTime && Date.parse(start) < Date.parse(c.now)) {
    const nowM = minuteOf(c.now, date, tz);
    const next = Math.ceil((nowM + 1) / 15) * 15;
    return refuse(
      `It’s ${timeText(c.now, tz)} now, so ${timeText(start, tz)} has passed. Move it to ${timeText(instantOfMinute(date, next, tz), tz)} or later.`,
    );
  }
  const lines: NoteLine[] = [];
  let look = false;
  if (tech) {
    const day = dayOf(tech, date);
    const away = (day?.blocks ?? []).find((b) => blockOverlaps(b, start, end, c.now));
    if (away)
      return refuse(
        away.allDay
          ? `${name} is away all day (${away.label.toLowerCase()}).`
          : `${name} is away then (${away.label.toLowerCase()}), so the visit would run into it.`,
      );
    const others = tech.visits.filter((o) => o.appointmentId !== v.appointmentId);
    const s = Date.parse(start);
    const e = Date.parse(end);
    const started = others.find(
      (o) =>
        (o.status === 'working' || o.status === 'paused' || o.status === 'closed') &&
        overlaps(s, e, Date.parse(o.start), Date.parse(o.end)),
    );
    if (started)
      return refuse(
        `${name} is at ${started.customerName} then, and one technician can only be on one visit at a time.`,
      );
    // a new technician: the licences it needs, checked as the server will (QA-691)
    if (!sameTech) {
      const gaps = licenceGapsAfter(c.board, v, c.fromTechId, tech.id, date, localDateOf(c.now, tz), tz);
      if (gaps.texts.length && gaps.known && c.board.licenceRule === 'refuse')
        return refuse(
          sentences(`${name} can’t take it: it needs a licence nobody on it would hold`, ...gaps.texts),
        );
      if (gaps.texts.length) {
        look = true;
        for (const text of gaps.texts) lines.push({ ok: false, text: `Needs a licence: ${text}` });
      }
    }
    // the drive, from our own quick estimate: from the visit before, and to the one after
    const before = others
      .filter((o) => Date.parse(o.end) <= s && o.status !== 'cancelled')
      .sort((a, b) => Date.parse(b.end) - Date.parse(a.end))[0];
    const after = others
      .filter((o) => Date.parse(o.start) >= e && o.status !== 'cancelled')
      .sort((a, b) => Date.parse(a.start) - Date.parse(b.start))[0];
    const legs: { other: BoardVisit; side: 'before' | 'after' }[] = [
      ...(before ? [{ other: before, side: 'before' as const }] : []),
      ...(after ? [{ other: after, side: 'after' as const }] : []),
    ];
    let checked = false;
    for (const { other, side } of legs) {
      if (!v.geo || !other.geo) {
        if (!checked) lines.push({ ok: null, text: 'Drive not checked: an address has no map point yet' });
        checked = true;
        continue;
      }
      const drive = driveMinutes(other.geo, v.geo);
      const gap = Math.round(
        (side === 'before' ? s - Date.parse(other.end) : Date.parse(other.start) - e) / 60_000,
      );
      const spare = gap - drive;
      if (spare < 0) {
        look = true;
        lines.push({
          ok: false,
          text: `${firstName(name)} can’t make it${side === 'after' ? ' to the next one' : ''}: about ${minutesText(drive)} drive, ${minutesText(-spare)} short`,
        });
      } else if (side === 'before') {
        lines.push({
          ok: true,
          text: `${firstName(name)} can make it: about ${minutesText(drive)} drive${spare < 60 ? `, ${minutesText(spare)} to spare` : ''}`,
        });
      }
      checked = true;
    }
    const hit = others.filter(
      (o) =>
        (o.status === 'scheduled' || o.status === 'dispatched') &&
        overlaps(s, e, Date.parse(o.start), Date.parse(o.end)),
    );
    if (hit.length) {
      look = true;
      lines.push({
        ok: false,
        text: `Runs into ${hit.map((o) => `${o.customerName} (${kernelWindow(o.start, o.end, tz)})`).join(', ')}`,
      });
    }
    if (day) {
      if (!day.workingHours.length) {
        look = true;
        lines.push({ ok: false, text: `${firstName(name)} isn’t working this day` });
      } else {
        const opens = Math.min(...day.workingHours.map((w) => Date.parse(w.start)));
        const closes = Math.max(...day.workingHours.map((w) => Date.parse(w.end)));
        if (e > closes) {
          look = true;
          lines.push({
            ok: false,
            text: `It ends ${timeText(end, tz)}, after ${firstName(name)}’s day ends at ${timeText(new Date(closes).toISOString(), tz)}`,
          });
        } else if (s < opens) {
          look = true;
          lines.push({
            ok: false,
            text: `It starts ${timeText(start, tz)}, before ${firstName(name)}’s day starts at ${timeText(new Date(opens).toISOString(), tz)}`,
          });
        }
      }
    }
    if (!sameTech && v.status === 'dispatched') {
      look = true;
      lines.push({ ok: false, text: 'It’s already on the way: whoever gets it now is sent at once' });
    }
  }
  if (!sameTime && !insideWindow(v, start)) {
    look = true;
    lines.push({
      ok: false,
      text: `Outside the time ${v.customerName} was given (${kernelWindow(v.arrivalWindow?.start ?? v.start, v.arrivalWindow?.end ?? v.end, tz)}): check it before anyone tells them`,
    });
  }
  if (len !== oldLen)
    lines.push({ ok: true, text: `${minutesText(len)} booked instead of ${minutesText(oldLen)}` });
  return { kind: look ? 'look' : 'clean', head, lines };
}

/**
 * QA-851: giving a waiting visit by pressing a technician's lane (or Enter on it) is the same move as
 * dropping it there at its own time, so it runs the drop's one check first: clean, it's saved with
 * Undo; needing a look (it runs into another visit, needs a licence nobody on it holds, runs past
 * their day), Check the move opens before anything is saved; can't, it says why. It never saves
 * first and warns after.
 */
export function laneGive(c: Omit<MoveCheck, 'to' | 'fromTechId'> & { techId: string }): {
  place: DragPlace;
  outcome: Outcome;
} {
  const { visit: v, date, timeZone: tz } = c;
  const place: DragPlace = {
    techId: c.techId,
    start: minuteOf(v.start, date, tz),
    end: minuteOf(v.end, date, tz),
  };
  return { place, outcome: classifyMove({ ...c, fromTechId: null, to: place }) };
}

/**
 * BL-158: a drop for a visit that has started or is over. Its technician and its day stay (another
 * lane can't), so only its times change. Started: its end not before now and its start not after now,
 * while work goes on. Over: always a look, the warning first, since it changes what's on record. What
 * it runs into, its technician's hours and their time away are said as a look, never refused: the
 * work happened (or is happening) when it did.
 */
function classifyRetime(
  c: MoveCheck,
  rule: 'started' | 'over',
  head: string,
  refuse: (reason: string, field?: 'start' | 'end') => Outcome,
): Outcome {
  const { visit: v, to, date, timeZone: tz } = c;
  const start = instantOfMinute(date, to.start, tz);
  const end = instantOfMinute(date, to.end, tz);
  const from = c.fromTechId ? c.board.technicians.find((t) => t.id === c.fromTechId) : undefined;
  const holder = from?.name ?? 'its technician';
  // over because its day went by, not because the work is done
  const gone = v.status !== 'closed' && v.pauseReason !== 'work_done';
  if (to.techId !== c.fromTechId)
    return refuse(
      rule === 'started'
        ? `Work has started at ${v.customerName}, so it stays with ${holder}. Only its times can change. To send someone else, add another visit.`
        : gone
          ? `${v.customerName}’s day has gone by, so the visit stays with ${holder}. Only its times can change.`
          : `${v.customerName} is done, so it stays with ${holder}. Only its times can change.`,
    );
  // where it is now: its booked times, or where it's drawn once work began (QA-844)
  const drawn = shownSpan(v);
  if ((start === v.start && end === v.end) || (start === drawn.start && end === drawn.end))
    return { kind: 'none', head, lines: [] };
  const nowAt = Date.parse(c.now);
  if (rule === 'started') {
    if (end !== v.end && Date.parse(end) < nowAt) {
      const next = Math.ceil((minuteOf(c.now, date, tz) + 1) / 15) * 15;
      return refuse(
        `Work is still going at ${v.customerName}, so it can’t end before now (${timeText(c.now, tz)}). End it at ${timeText(instantOfMinute(date, next, tz), tz)} or later.`,
        'end',
      );
    }
    if (start !== v.start && Date.parse(start) > nowAt)
      return refuse(
        `Work has started at ${v.customerName}, so its start can’t be after now (${timeText(c.now, tz)}).`,
        'start',
      );
  }
  const lines: NoteLine[] = [];
  if (rule === 'over')
    lines.push({
      ok: false,
      text: gone
        ? `${v.customerName}’s day has gone by: this changes the times on its record`
        : `${v.customerName} is done: this changes the times on its record`,
    });
  let look = rule === 'over';
  const tech = c.board.technicians.find((t) => t.id === to.techId);
  if (tech) {
    const s = Date.parse(start);
    const e = Date.parse(end);
    const hit = tech.visits.filter(
      (o) =>
        o.appointmentId !== v.appointmentId &&
        o.status !== 'cancelled' &&
        overlaps(s, e, Date.parse(o.start), Date.parse(o.end)),
    );
    if (hit.length) {
      look = true;
      lines.push({
        ok: false,
        text: `Runs into ${hit.map((o) => `${o.customerName} (${kernelWindow(o.start, o.end, tz)})`).join(', ')}`,
      });
    }
    const day = dayOf(tech, date);
    const away = (day?.blocks ?? []).find((b) => !b.allDay && blockOverlaps(b, start, end, c.now));
    if (away) {
      look = true;
      lines.push({ ok: false, text: `Runs into ${firstName(holder)}’s ${away.label.toLowerCase()}` });
    }
    if (rule === 'started' && day?.workingHours.length) {
      const closes = Math.max(...day.workingHours.map((w) => Date.parse(w.end)));
      if (e > closes) {
        look = true;
        lines.push({
          ok: false,
          text: `It ends ${timeText(end, tz)}, after ${firstName(holder)}’s day ends at ${timeText(new Date(closes).toISOString(), tz)}`,
        });
      }
    }
  }
  const oldLen = Math.round((Date.parse(v.end) - Date.parse(v.start)) / 60_000);
  const len = to.end - to.start;
  if (len !== oldLen) lines.push({ ok: true, text: `${minutesText(len)} instead of ${minutesText(oldLen)}` });
  return { kind: look ? 'look' : 'clean', head, lines, retime: rule };
}

/**
 * The visit panel's Change times (BL-158): a started or finished visit's start and end typed ("10:15",
 * "12:30p"), on the visit's own day, checked by the drag's own rules. A field that isn't a time says
 * so under it; otherwise the check (its refusal goes under the end), and the instants to send.
 */
export function typedTimes(
  board: MoveCheck['board'],
  v: BoardVisit,
  text: { start: string; end: string },
  now: string,
  timeZone: string,
): {
  errors: { start?: string; end?: string };
  outcome: Outcome | null;
  to: { start: string; end: string } | null;
} {
  const date = localDateOf(v.start, timeZone);
  const s = parseTypedTime(text.start, minuteOf(v.start, date, timeZone), { nextDay: false });
  const e = parseTypedTime(text.end, minuteOf(v.end, date, timeZone));
  const errors: { start?: string; end?: string } = {};
  if (!s.ok) errors.start = s.error;
  if (!e.ok) errors.end = e.error;
  if (!s.ok || !e.ok) return { errors, outcome: null, to: null };
  if (e.minute <= s.minute)
    return { errors: { end: 'It has to end after it starts.' }, outcome: null, to: null };
  const lane = v.leadTechId ?? v.techIds[0] ?? null;
  const outcome = classifyMove({
    board,
    visit: v,
    fromTechId: lane,
    to: { techId: lane, start: s.minute, end: e.minute },
    date,
    now,
    timeZone,
  });
  // a refusal sits on the field it's about: a start after now on Started, the rest on Ends (QA-766)
  if (outcome.kind === 'refuse')
    return { errors: { [outcome.field ?? 'end']: outcome.reason! }, outcome, to: null };
  return {
    errors,
    outcome,
    to: {
      start: instantOfMinute(date, s.minute, timeZone),
      end: instantOfMinute(date, e.minute, timeZone),
    },
  };
}

function blockOverlaps(b: TechBlock, start: string, end: string, now: string): boolean {
  if (b.allDay) return true;
  const bEnd = b.end ?? (Date.parse(now) > Date.parse(b.start) ? now : b.start);
  return overlaps(Date.parse(start), Date.parse(end), Date.parse(b.start), Date.parse(bEnd));
}

/**
 * Whether a new start keeps to what the customer was told: inside the arrival window they were given,
 * or, with none, within half an hour of the start they were given.
 */
export function insideWindow(v: Pick<BoardVisit, 'start' | 'arrivalWindow'>, start: string): boolean {
  const s = Date.parse(start);
  if (v.arrivalWindow) return s >= Date.parse(v.arrivalWindow.start) && s <= Date.parse(v.arrivalWindow.end);
  return Math.abs(s - Date.parse(v.start)) <= 30 * 60_000;
}

/** The note's last line: what letting go does, in the words of the hand doing it (a started visit's: its times). */
export function footFor(kind: OutcomeKind, how: DragHow, retime?: Outcome['retime']): string {
  if (kind === 'none') return 'Where it is now';
  if (kind === 'refuse') return 'It can’t go here';
  const verb = how === 'key' ? 'Press Enter' : how === 'touch' ? 'Lift your finger' : 'Let go';
  if (retime)
    return kind === 'clean' ? `${verb} to change its times` : `${verb} to check the new times first`;
  return kind === 'clean' ? `${verb} to move it` : `${verb} to check the move first`;
}

/** The note in one breath, for the screen reader's live region. */
export function noteText(o: Outcome, how: DragHow): string {
  return [o.head, ...o.lines.map((l) => l.text), footFor(o.kind, how, o.retime)]
    .map((x) => x.replace(/\.$/, ''))
    .join('. ')
    .concat('.');
}

/** The arrival window for a move that leaves the old one: the same length, moved with the start. */
export function shiftedWindow(
  v: Pick<BoardVisit, 'start' | 'arrivalWindow'>,
  start: string,
): { start: string; end: string } | null {
  if (!v.arrivalWindow) return null;
  const delta = Date.parse(start) - Date.parse(v.start);
  const at = (x: string) => new Date(Date.parse(x) + delta).toISOString();
  return { start: at(v.arrivalWindow.start), end: at(v.arrivalWindow.end) };
}

export interface MovePlan {
  /** appointments.reschedule's input without its rowVersion, when the time changes */
  reschedule: {
    appointmentId: string;
    start: string;
    end: string;
    arrivalWindow?: { start: string; end: string };
  } | null;
  /** appointments.assign's input without its rowVersion, when the technician changes */
  assign: { appointmentId: string; techIds: string[] } | null;
  /**
   * BL-158: appointments.retime's input without its rowVersion, a started or finished visit's new
   * times (confirm: the warning was seen, for one that is over)
   */
  retime?: { appointmentId: string; start: string; end: string; confirm?: true } | null;
}

/**
 * The actions a move sends, the time first, then who. A clean drop keeps the arrival window; a move
 * the panel checked outside it takes `window` (shiftedWindow's, unless the person says otherwise).
 */
export function movePlan(
  v: BoardVisit,
  fromTechId: string | null,
  to: { techId: string | null; start: string; end: string },
  window?: { start: string; end: string } | null,
  rule: TimeRule = 'free',
): MovePlan {
  const timeChanged = to.start !== v.start || to.end !== v.end;
  // a started or finished visit: its times only, never who or its window (BL-158)
  if (rule === 'started' || rule === 'over')
    return {
      reschedule: null,
      assign: null,
      retime: timeChanged
        ? {
            appointmentId: v.appointmentId,
            start: to.start,
            end: to.end,
            ...(rule === 'over' ? { confirm: true as const } : {}),
          }
        : null,
    };
  const r =
    to.techId && to.techId !== fromTechId
      ? dropResult(v, fromTechId, { kind: 'tech', techId: to.techId })
      : null;
  return {
    reschedule: timeChanged
      ? {
          appointmentId: v.appointmentId,
          start: to.start,
          end: to.end,
          ...(window ? { arrivalWindow: window } : {}),
        }
      : null,
    assign: r?.kind === 'assign' ? { appointmentId: v.appointmentId, techIds: r.input.techIds } : null,
  };
}

/**
 * Undo puts it back as it was: its technicians (the lead too), then its times and window, each sent
 * with the rowVersion the last action answered, so anyone else's change since is refused, not lost.
 */
export function undoPlan(
  was: BoardVisit,
  plan: MovePlan,
): {
  assign: { appointmentId: string; techIds: string[]; leadTechId?: string } | null;
  reschedule: {
    appointmentId: string;
    start: string;
    end: string;
    arrivalWindow?: { start: string; end: string } | null;
  } | null;
  retime: { appointmentId: string; start: string; end: string; confirm?: true } | null;
} {
  return {
    // a started or finished visit's times back as they were, confirmed as the change was
    retime: plan.retime
      ? {
          appointmentId: was.appointmentId,
          start: was.start,
          end: was.end,
          ...(plan.retime.confirm ? { confirm: true as const } : {}),
        }
      : null,
    assign: plan.assign
      ? {
          appointmentId: was.appointmentId,
          techIds: was.techIds,
          ...(was.leadTechId ? { leadTechId: was.leadTechId } : {}),
        }
      : null,
    reschedule: plan.reschedule
      ? {
          appointmentId: was.appointmentId,
          start: was.start,
          end: was.end,
          ...(plan.reschedule.arrivalWindow ? { arrivalWindow: was.arrivalWindow } : {}),
        }
      : null,
  };
}

/**
 * QA-766: the panel's Change times (and its "Change them anyway") as a move's plan, so its toast offers
 * the same Undo a drop's does; undoPlan puts the times back, confirmed as the change was.
 */
export function retimePlan(
  v: Pick<BoardVisit, 'appointmentId'>,
  to: { start: string; end: string; confirm?: true },
): MovePlan {
  return {
    reschedule: null,
    assign: null,
    retime: {
      appointmentId: v.appointmentId,
      start: to.start,
      end: to.end,
      ...(to.confirm ? { confirm: true as const } : {}),
    },
  };
}

/** The toast after a started or finished visit's times changed (BL-158): "Pacific Rim Cafe is now 10 a.m.–12:30 p.m.". */
export function retimedLine(
  v: Pick<BoardVisit, 'customerName'>,
  to: { start: string; end: string },
  timeZone: string,
) {
  return sentences(
    `${v.customerName} is now ${kernelWindow(to.start, to.end, timeZone)}`,
    'Nobody was texted',
  );
}

/**
 * QA-842: what a give says, on the board with its Undo as well as to a screen reader: "Lindqvist
 * residence's visit given to Sam Rivera, 2:30 p.m." (its own start, as its block says it, QA-762;
 * helpers joined with "and"), or back in Waiting when nobody has it now.
 */
export function givenLine(
  v: Pick<BoardVisit, 'customerName' | 'start'>,
  names: readonly string[],
  timeZone: string,
): string {
  if (!names.length) return `${v.customerName}’s visit is back in Waiting.`;
  const c = clock(v.start, timeZone);
  return sentences(`${v.customerName}’s visit given to ${names.join(' and ')}, ${c.time} ${c.suffix}`);
}

/**
 * QA-846: the visit changed on another screen while its Check the move was open. The check (and the
 * time it offered, worked out from where the visit was) is off: it closes, saying where the visit is
 * now, so nobody's move is overwritten by one made from an old board.
 */
export function changedUnderCheck(
  was: Pick<BoardVisit, 'customerName' | 'start' | 'end' | 'techIds'>,
  now: Pick<BoardVisit, 'customerName' | 'start' | 'end' | 'techIds'>,
  timeZone: string,
  techName: (id: string) => string,
): string {
  const moved = was.start !== now.start || was.end !== now.end;
  const who = now.techIds.length ? now.techIds.map(techName).join(' and ') : 'no one yet';
  const changedHands = was.techIds.join() !== now.techIds.join();
  const where = [
    moved ? kernelWindow(now.start, now.end, timeZone) : null,
    changedHands || !moved ? who : null,
  ]
    .filter(Boolean)
    .join(', ');
  return sentences(
    `${now.customerName} was just changed on another screen: it’s ${where} now`,
    'So it wasn’t moved. Move it again from there if you still want to',
  );
}

/** QA-848: how long a move has to be answered, every try included, before the board says it wasn't. */
export const SAVE_WITHIN_MS = 20_000;
/** QA-848: how long a move goes unanswered before the board says it's still trying. */
export const SLOW_SAVE_MS = 4_000;
/** What a move's save came to: in, refused (said in the toast), or no answer in SAVE_WITHIN_MS. */
export type MoveResult = 'moved' | 'refused' | 'no-answer';

/** QA-848: said while a move is out longer than SLOW_SAVE_MS. */
export const STILL_SAVING =
  'Still saving. Check your connection: it stays where it was until this says it moved.';

/**
 * QA-848: a move that got no answer in SAVE_WITHIN_MS. It may have landed: Try again sends it with the
 * same key, so it's done once either way.
 */
export function noAnswerLine(
  v: Pick<BoardVisit, 'customerName' | 'start' | 'end'>,
  timeZone: string,
): string {
  return sentences(
    'Not moved yet: the connection isn’t answering',
    `${v.customerName} shows ${kernelWindow(v.start, v.end, timeZone)} until the board hears otherwise`,
    'Try again: it’s only done once',
  );
}

/**
 * QA-853: a move sent while offline, waiting to go: said on the board until it's answered, so a give
 * that hasn't gone yet doesn't look as if the press didn't take. It isn't saved yet, and says so.
 */
export function waitingToSendLine(
  v: Pick<BoardVisit, 'customerName' | 'techIds'>,
  place: Pick<DragPlace, 'techId'>,
  techName: (id: string) => string,
): string {
  const giving = place.techId && !v.techIds.includes(place.techId);
  return sentences(
    giving
      ? `Waiting to send: ${v.customerName}’s visit to ${techName(place.techId!)}`
      : `Waiting to send: ${v.customerName}’s move`,
    'Not saved yet. It goes when you’re back online, and this says when it has',
  );
}

/** QA-853: a second give of a visit whose last one is still being sent: refused, so none is replayed twice. */
export const STILL_SENDING = 'Still sending the last change to this visit. Wait for it, then try again.';

/** The toast after a clean drop: "Moved Gorge Vale residence to 12:30 p.m." / "… to Sam Kowal, 1 p.m.". */
export function movedLine(
  v: Pick<BoardVisit, 'customerName' | 'start' | 'end'>,
  to: { techName: string | null; start: string; end: string },
  timeZone: string,
): string {
  const timeChanged = to.start !== v.start;
  const lengthOnly = !timeChanged && to.end !== v.end;
  if (lengthOnly && !to.techName)
    return sentences(`${v.customerName} is now ${kernelWindow(to.start, to.end, timeZone)}`);
  const when = timeChanged ? timeText(to.start, timeZone) : null;
  const what = [to.techName, when].filter(Boolean).join(', ');
  return sentences(
    `Moved ${v.customerName} to ${what || kernelWindow(to.start, to.end, timeZone)}`,
    'Nobody was texted',
  );
}
